import {
  and,
  collection,
  DocumentData,
  getDocs,
  getFirestore,
  onSnapshot,
  or,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  where
} from 'firebase/firestore';
import app from '../firebaseConfig';
import {DEPARTMENTS, Department, IFirestoreTimeOffRequest, ITimeOffRequest, RequestStatus, TimeOffDepartment} from './interfaces';
import {getEmployeeDetailsByRef} from './getEmployeeDetailsByRef';
import {firestoreCollections} from './firestoreConstants';
import {CALENDAR_EVENT_SYMBOLS} from '../utils/calendarEventSymbols';


const db = getFirestore(app);

/**
 * Fetches time off requests within a specified date range for a given department
 * and sets up real-time updates.
 *
 * @param {Date} start - The start date of the range.
 * @param {Date} end - The end date of the range.
 * @param {Department} department - The department to filter time off requests by.
 * @param {(events: ITimeOffRequest[]) => void} onTimeOffUpdate - Callback function to handle updates to the list of time off entries.
 * @returns {Promise<() => void>} - A function to unsubscribe from real-time updates when called.
 */
export async function fetchTimeOffForRange(
  start: Date,
  end: Date,
  department: Department,
  onTimeOffUpdate: (events: ITimeOffRequest[]) => void
): Promise<() => void> {
  // Convert dates to 'YYYY-MM-DD' format for querying Firestore
  const startOfRangeString = start.toISOString().split("T")[0];
  const endOfRangeString = end.toISOString().split("T")[0];

  // Reference the time off requests collection in Firestore
  const timeOffCollectionRef = collection(db, firestoreCollections.timeOffRequests);

  // Create a query to fetch approved time off requests where:
  // (startDate is within the range AND department matches) OR
  // (endDate is within the range AND department matches)
  const q = query(
    timeOffCollectionRef,
    or(
      and(
        where("startDate", ">=", startOfRangeString),
        where("startDate", "<=", endOfRangeString),
        where("status", "==", "approved" as RequestStatus)
      ),
      and(
        where("endDate", ">=", startOfRangeString),
        where("endDate", "<=", endOfRangeString),
        where("status", "==", "approved" as RequestStatus)
      ),
      and(
        where("startDate", "<", startOfRangeString),
        where("endDate", ">", endOfRangeString),
        where("status", "==", "approved" as RequestStatus)
      )
    )
  );

  /**
   * Processes a Firestore snapshot by converting documents to time off requests
   * and passing them to the update callback.
   *
   * @param {QuerySnapshot} snapshot - Firestore snapshot of documents matching the query.
   */
  const processSnapshot = async (snapshot: QuerySnapshot<DocumentData>) => {
    // Convert Firestore documents to time off request objects
    const timeOffRequests = await convertTimeOff(snapshot.docs);
    // Filter the requests by department
    const filteredTimeOffRequests = timeOffRequests.filter((request) => request.department === department);
    // Trigger the callback to update the requests
    onTimeOffUpdate(filteredTimeOffRequests);
  };

  // Fetch initial data when the function is called and process it
  const initialSnapshot = await getDocs(q);
  await processSnapshot(initialSnapshot);

  // Set up a real-time listener for changes to the queried time off requests and process updates
  // Return the unsubscribe function to allow the caller to stop listening for updates
  return onSnapshot(q, processSnapshot);
}


/**
 * Converts Firestore documents to ITimeOffRequest objects, filtering by department.
 *
 * This function processes all documents in parallel to improve performance.
 *
 * @param {QueryDocumentSnapshot<DocumentData>[]} docs - The Firestore documents to convert.
 * @returns {Promise<ITimeOffRequest[]>} An array of ITimeOffRequest objects.
 */
async function convertTimeOff(docs: QueryDocumentSnapshot<DocumentData>[]): Promise<ITimeOffRequest[]> {
  // Map over the documents to create an array of Promises for time off request conversions
  const timeOffPromises: Promise<ITimeOffRequest | null>[] = docs.map(async (doc) => {
    try {
      const docData = doc.data() as IFirestoreTimeOffRequest;

      // Start fetching traveler and crew data concurrently
      const employee = await getEmployeeDetailsByRef(docData.employee);

      const departmentString = employee?.departmentString || '';

      let department: TimeOffDepartment = "Other";
switch (departmentString) {
  case DEPARTMENTS.customerService:
    department = DEPARTMENTS.customerService;
    break;

  case DEPARTMENTS.installation:
    department = DEPARTMENTS.installation;
    break;

  case DEPARTMENTS.assembly:
    department = DEPARTMENTS.assembly;
    break;

  case DEPARTMENTS.production:
    department = DEPARTMENTS.production;
    break;

  default:
    department = "Other";
    break;
}
      // Add an extra day to the end date
      const endDate = new Date(docData.endDate);
      endDate.setDate(endDate.getDate() + 1);

      // Set the event colors
      const backgroundColor = "#9ae194";
      const borderColor = backgroundColor;
      const textColor = "#000";

      return {
        ...docData,
        endDate: endDate.toISOString().split("T")[0], // Convert back to 'YYYY-MM-DD' format
        employeeName: CALENDAR_EVENT_SYMBOLS.dayOff + (employee?.preferredName || "Error loading request"),
        department: department,
        backgroundColor,
        borderColor,
        textColor,
      };

    } catch (error) {
      // Log the error and continue processing other documents
      console.error(`Error processing time off request ${doc.id}:`, error);
      return null; // Skip this request in case of an error
    }
  });

  // Wait for all request conversion Promises to resolve
  const timeOffRequests = await Promise.all(timeOffPromises);

  // Filter out any null results (requests that were skipped)
  return timeOffRequests.filter((request): request is ITimeOffRequest => request !== null);
}
